import { Network } from 'lucide-react';
import { Card } from '@/components/Card';
import { useApi } from '@/hooks/useApi';

interface Department {
  id: string;
  name: string;
  head_name?: string;
  employee_count: number;
  parent_id?: string;
}

interface OrgEmployee {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  department_name?: string;
  manager_id?: string;
  avatar_url?: string;
}

export function OrgChartPage() {
  const { data, loading } = useApi<{ departments: Department[]; employees: OrgEmployee[] }>('org-chart');

  if (loading) return <div className="text-muted-foreground">Loading...</div>;

  const departments = data?.departments ?? [];
  const employees = data?.employees ?? [];

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <Network className="w-5 h-5 text-amber-500" /> Organization Chart
      </h2>

      {/* Department cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {departments.map(dept => {
          const deptEmployees = employees.filter(e => e.department_name === dept.name);
          return (
            <Card key={dept.id}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-foreground">{dept.name}</h3>
                <span className="text-xs text-muted-foreground">{dept.employee_count} people</span>
              </div>
              {dept.head_name && <p className="text-xs text-amber-500 mb-2">Led by {dept.head_name}</p>}
              <div className="space-y-1">
                {deptEmployees.slice(0, 5).map(e => (
                  <div key={e.id} className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-surface-3 flex items-center justify-center text-[10px] text-muted-foreground font-medium">
                      {e.first_name[0]}{e.last_name[0]}
                    </div>
                    <div>
                      <p className="text-xs text-foreground">{e.first_name} {e.last_name}</p>
                      <p className="text-[10px] text-muted-foreground">{e.title}</p>
                    </div>
                  </div>
                ))}
                {deptEmployees.length > 5 && (
                  <p className="text-xs text-muted-foreground">+{deptEmployees.length - 5} more</p>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {departments.length === 0 && (
        <Card><p className="text-sm text-muted-foreground">No departments configured. Add departments in Admin settings.</p></Card>
      )}
    </div>
  );
}
